import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { contractKeys } from "@/hooks/api/useContracts";
import type { paths } from "@/types/api";

type AdjustmentBody =
  paths["/api/v1/contracts/{contract_id}/cost-adjustments"]["post"]["requestBody"]["content"]["application/json"];

const key = (id: string) => ["contracts", id, "cost-adjustments"] as const;

export function useCostAdjustments(contractId: string | undefined) {
  return useQuery({
    queryKey: key(contractId ?? ""),
    enabled: Boolean(contractId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/contracts/{contract_id}/cost-adjustments",
        { params: { path: { contract_id: contractId! } } },
      );
      if (error || !data) throw new Error("Failed to load cost adjustments");
      return data;
    },
  });
}

export function useCreateCostAdjustment(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdjustmentBody) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/contracts/{contract_id}/cost-adjustments",
        { params: { path: { contract_id: contractId } }, body },
      );
      if (error || !data) throw new Error("Failed to record adjustment");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key(contractId) });
      qc.invalidateQueries({ queryKey: ["contracts", contractId, "billing"] });
      qc.invalidateQueries({ queryKey: contractKeys.detail(contractId) });
    },
  });
}
